<template>
    <!-- HERO -->
    <section id="hero">
        <div id="hero-content" class="container" data-aos="fade-up">
            <v-row justify="center" data-aos="fade-up" data-aos-delay="150">
                <v-col xl="10" lg="9" cols="12">
                    <h1 class="hero-top-title">
                        {{ terms.slogon }} <span class="hero-brand-inline">{{ terms.brand }}</span>
                    </h1>
                    <div class="hero-image-spacer"></div>
                    <h2 class="hero-subtitle">{{ terms.team_description }}</h2>
                </v-col>
            </v-row>
        </div>
    </section>

    <!-- ABOUT -->
    <section id="about" class="restricted-width">
        <v-container data-aos="fade-up">
            <v-row>
                <div class="section-title">
                    <h2>{{ terms.menu_about }}</h2>
                </div>
            </v-row>
            <v-row>
                <v-col lg="4" order="2" order-lg="1" data-aos="fade-left" data-aos-delay="100">
                    <video id="VidAbout" class="w-100 signature-media-frame" :src="videoPaths.about" controls muted loop playsinline preload="metadata" poster="/img/tape_dispenser_new.jpg"></video>
                    <div class="text-center">
                        <button v-if="$route.name !== 'offers'" class="order-now-btn"
                            @click="navigateToRoute('/offers')">{{ terms.menu_order_now }}</button>
                    </div>
                </v-col>
                <v-col cols="12" lg="8" order="1" order-lg="2" class="pt-4 pt-lg-0 content" data-aos="fade-right"
                    data-aos-delay="100">
                    <h3>{{ terms.about_header }}</h3>
                    <p class="fst-italic">{{ terms.about_description }}</p>
                    <ul class="about-topic-list">
                        <li>
                            <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                            <span>{{ terms.about_topic1 }}</span>
                        </li>
                        <li>
                            <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                            <span>{{ terms.about_topic2 }}</span>
                        </li>
                        <li>
                            <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                            <span>{{ terms.about_topic3 }}</span>
                        </li>
                        <li>
                            <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                            <span>{{ terms.about_topic4 }}</span>
                        </li>
                    </ul>
                </v-col>
            </v-row>

            <!-- PRODUCTS / OUR PRODUCT DETAILS -->
            <v-row id="products" class="mt-8 pt-2">
                <v-col cols="12" lg="4" order="2" order-lg="1" data-aos="fade-right" data-aos-delay="100">
                    <img class="d-block w-100 signature-media-frame mb-6" src="/img/products_11.jpg" :alt="terms.product_dispenser_alt" />
                    <video id="VidProducts" class="w-100 signature-media-frame mt-6" :src="videoPaths.products" controls muted preload="metadata" poster="/img/products_11.jpg"></video>
                    <div class="text-center">
                        <button class="order-now-btn" @click="navigateToRoute('/offers')">{{ terms.menu_order_now }}</button>
                    </div>
                </v-col>
                <v-col cols="12" lg="8" order="1" order-lg="2" class="pt-4 pt-lg-0 content" data-aos="fade-left"
                    data-aos-delay="100">
                    <!-- Product Header & Lead -->
                    <div class="product-header-block mb-3">
                        <h3 class="product-main-heading">{{ terms.product_headline }}</h3>
                        <p class="product-lead-text">{{ terms.product_lead }}</p>
                    </div>

                    <!-- Introduction Paragraphs -->
                    <div class="product-description-block mb-4">
                        <p class="product-desc-p mb-2">{{ terms.product_intro1 }}</p>
                        <p class="product-desc-p mb-3">{{ terms.product_intro2 }}</p>
                    </div>

                    <!-- Why Choose Section -->
                    <div class="product-why-section mb-4">
                        <h4 class="product-subheading d-flex align-center ga-2 mb-3">
                            <span>💡</span>
                            <span>{{ terms.product_why_title }}</span>
                        </h4>
                        <ul class="product-feature-list">
                            <li>
                                <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                <span><strong>{{ terms.product_why_f1_title }}</strong> {{ terms.product_why_f1_desc }}</span>
                            </li>
                            <li>
                                <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                <span><strong>{{ terms.product_why_f2_title }}</strong> {{ terms.product_why_f2_desc }}</span>
                            </li>
                            <template v-if="$vuetify.display.mdAndUp || showMoreProductText">
                                <li>
                                    <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                    <span><strong>{{ terms.product_why_f3_title }}</strong> {{ terms.product_why_f3_desc }}</span>
                                </li>
                                <li>
                                    <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                    <span><strong>{{ terms.product_why_f4_title }}</strong> {{ terms.product_why_f4_desc }}</span>
                                </li>
                                <li>
                                    <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                    <span><strong>{{ terms.product_why_f5_title }}</strong> {{ terms.product_why_f5_desc }}</span>
                                </li>
                                <li>
                                    <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                    <span><strong>{{ terms.product_why_f6_title }}</strong> {{ terms.product_why_f6_desc }}</span>
                                </li>
                            </template>
                        </ul>
                    </div>

                    <!-- Mobile Show more toggle -->
                    <div v-if="!$vuetify.display.mdAndUp" class="text-center mt-3 mb-2">
                        <button type="button" class="show-more-toggle-btn" @click="toggleShowMoreProductText">
                            <span>
                                {{ showMoreProductText ? (terms.show_less || 'Show less') : (terms.show_more || 'Show more') }}
                            </span>
                            <v-icon size="small" class="ml-1">
                                {{ showMoreProductText ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                        </button>
                    </div>
                </v-col>

                <!-- Overflowing Content / Technical Specifications & Business (Full Width below media) -->
                <v-col v-if="$vuetify.display.mdAndUp || showMoreProductText" cols="12" class="pt-6 content" data-aos="fade-up">
                    <v-row>
                        <!-- Technical Specifications -->
                        <v-col cols="12" md="5" class="pr-md-6">
                            <div class="product-specs-section mb-4">
                                <h4 class="product-subheading d-flex align-center ga-2 mb-3">
                                    <span>⚙️</span>
                                    <span>{{ terms.product_specs_title }}</span>
                                </h4>
                                <ul class="product-specs-list mb-3">
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_spec_weight_label }}</strong> {{ terms.product_spec_weight_val }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_spec_width_label }}</strong> {{ terms.product_spec_width_val }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_spec_materials_label }}</strong> {{ terms.product_spec_materials_val }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_spec_suitable_label }}</strong> {{ terms.product_spec_suitable_val }}</span>
                                    </li>
                                </ul>
                                <div class="specs-footer-callout pt-3 pb-1 d-flex flex-wrap align-center justify-space-between ga-2">
                                    <span class="specs-tagline">{{ terms.product_specs_tagline }}</span>
                                    <span class="specs-contact">
                                        <v-icon size="16" class="mr-1" style="margin-top: -2px;">mdi-phone</v-icon>
                                        {{ terms.product_specs_contact_label }}
                                        <a :href="`mailto:${terms.contact_email_address || 'info@avembg.com'}`">{{ terms.contact_email_address || 'info@avembg.com' }}</a>
                                    </span>
                                </div>
                            </div>
                        </v-col>

                        <!-- AVEM for Business -->
                        <v-col cols="12" md="7" class="pl-md-6">
                            <div class="product-b2b-section mb-4">
                                <h4 class="product-subheading d-flex align-center ga-2 mb-3">
                                    <span>💼</span>
                                    <span>{{ terms.product_b2b_title }}</span>
                                </h4>
                                <p class="product-desc-p mb-4">{{ terms.product_b2b_intro }}</p>

                                <h4 class="product-subheading d-flex align-center ga-2 mb-3">
                                    <span>🚀</span>
                                    <span>{{ terms.product_b2b_why_title }}</span>
                                </h4>
                                <ul class="product-b2b-list">
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_b2b_p1_title }}</strong> {{ terms.product_b2b_p1_desc }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_b2b_p2_title }}</strong> {{ terms.product_b2b_p2_desc }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_b2b_p3_title }}</strong> {{ terms.product_b2b_p3_desc }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_b2b_p4_title }}</strong> {{ terms.product_b2b_p4_desc }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_b2b_p5_title }}</strong> {{ terms.product_b2b_p5_desc }}</span>
                                    </li>
                                    <li>
                                        <v-icon size="18" color="#ffc451" class="flex-shrink-0 mt-1">mdi-check-circle-outline</v-icon>
                                        <span><strong>{{ terms.product_b2b_p6_title }}</strong> {{ terms.product_b2b_p6_desc }}</span>
                                    </li>
                                </ul>
                            </div>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <!-- Offers Section with Interactive Product Grid & Signature Styling -->
            <div id="products-list" class="mt-6 mt-md-8 border-top pt-6 pt-md-8">
                <h2 class="offers-outer-title text-center">
                    {{ terms.offers_explore_all_title }}
                </h2>
                <div class="signature-offers-card">
                    <!-- Interactive Product Tiles Grid with Mobile Swipe -->
                    <div
                        class="swipeable-container"
                        :class="offersSwipe.bounceClass.value"
                        @touchstart="offersSwipe.onTouchStart"
                        @touchend="offersSwipe.onTouchEnd"
                        @click.capture="offersSwipe.onClickCapture"
                    >
                        <transition :name="offersSwipe.transitionName.value" mode="out-in">
                            <div :key="offersCurrentPage" class="product-tiles-grid">
                                <div
                                    v-for="item in paginatedOffersProducts"
                                    :key="item.id"
                                    :class="['interactive-product-tile', { 'offers-placeholder-tile': item.isPlaceholder }]"
                                    @click="!item.isPlaceholder && navigateToProductOffer(item.id)"
                                    :title="!item.isPlaceholder ? getBannerProductTitle(item) : ''"
                                >
                                    <template v-if="!item.isPlaceholder">
                                        <div class="tile-image-bg" :style="{ backgroundImage: `url('${item.image}')` }"></div>
                                        <div class="tile-badge">
                                            <span class="tile-title">{{ getBannerProductTitle(item) }}</span>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <!-- Offers Pagination on Small Devices -->
                    <v-row v-if="totalOffersPages > 1" justify="center" class="mt-3">
                        <v-pagination
                            v-model="offersCurrentPage"
                            :length="totalOffersPages"
                            :total-visible="5"
                            color="avem"
                            active-color="avem"
                            rounded="circle"
                            class="gallery-pagination"
                        ></v-pagination>
                    </v-row>
                </div>
            </div>
        </v-container>
    </section>

    <!-- Toast Notification for Add to Cart -->
    <v-snackbar v-model="snackbar" :timeout="3000" color="success" location="bottom right">
        <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-check-circle</v-icon>
            <span>{{ snackbarText }}</span>
        </div>
        <template v-slot:actions>
            <v-btn color="white" variant="text" @click="goToCart" :title="terms.view_cart">
                <v-icon class="d-inline d-sm-none" size="22">mdi-cart</v-icon>
                <span class="d-none d-sm-inline">{{ terms.view_cart }}</span>
            </v-btn>
        </template>
    </v-snackbar>

    <!-- PORTFOLIO -->
    <section id="portfolio" class="restricted-width">
        <v-container data-aos="fade-up">
            <v-row>
                <div class="section-title">
                    <h2>{{ terms.menu_gallery }}</h2>
                </div>
            </v-row>
            <div
                class="swipeable-container"
                :class="gallerySwipe.bounceClass.value"
                @touchstart="gallerySwipe.onTouchStart"
                @touchend="gallerySwipe.onTouchEnd"
                @click.capture="gallerySwipe.onClickCapture"
            >
                <transition :name="gallerySwipe.transitionName.value" mode="out-in">
                    <v-row :key="currentPage">
                        <v-col v-for="(item, localIndex) in paginatedGalleryImages" :key="localIndex" cols="6" sm="4" md="3" class="pa-2 pa-sm-3">
                            <v-img
                                v-if="!item.isPlaceholder"
                                :src="item.src"
                                aspect-ratio="1.5"
                                bg-color="#ffffff"
                                class="lightbox-image"
                                @click="openLightboxFromPage(localIndex)"
                            ></v-img>
                            <div v-else class="gallery-placeholder-slot"></div>
                        </v-col>
                    </v-row>
                </transition>
            </div>

            <!-- Signature Gallery Pagination -->
            <v-row v-if="totalGalleryPages > 1" justify="center" class="mt-2 mt-sm-4">
                <v-pagination
                    v-model="currentPage"
                    :length="totalGalleryPages"
                    :total-visible="5"
                    color="avem"
                    active-color="avem"
                    rounded="circle"
                    class="gallery-pagination"
                ></v-pagination>
            </v-row>
            <v-dialog v-model="isLightboxOpen" fullscreen persistent transition="dialog-fade-transition">
                <v-container
                    class="lightbox-container"
                    fluid
                    @touchstart="lightboxSwipe.onTouchStart"
                    @touchend="lightboxSwipe.onTouchEnd"
                    @click.capture="lightboxSwipe.onClickCapture"
                >
                    <!-- Small Circular Close Button -->
                    <button type="button" class="lightbox-close-circle-btn" @click="isLightboxOpen = false" title="Close">
                        <v-icon size="small">mdi-close</v-icon>
                    </button>

                    <!-- Left Signature Arrow -->
                    <button type="button" class="lightbox-nav-btn lightbox-prev-btn" @click="prevImage" title="Previous">
                        <v-icon size="large">mdi-chevron-left</v-icon>
                    </button>

                    <!-- Image Display Box with Signature Frame -->
                    <div class="lightbox-image-wrapper">
                        <img :src="allGalleryImages[activeImageIndex]" class="lightbox-active-img" :alt="terms.gallery_image_alt || 'Gallery image'" />
                    </div>

                    <!-- Right Signature Arrow -->
                    <button type="button" class="lightbox-nav-btn lightbox-next-btn" @click="nextImage" title="Next">
                        <v-icon size="large">mdi-chevron-right</v-icon>
                    </button>
                </v-container>
            </v-dialog>
        </v-container>
    </section>

    <!-- CONTACT -->
    <section id="contact" class="restricted-width">
        <v-container class="container" data-aos="fade-up">
            <v-row>
                <div class="section-title">
                    <h2>{{ terms.menu_contact }}</h2>
                </div>
            </v-row>
            <v-row class="mt-2">
                <v-col cols="12" sm="4">
                    <div class="info">
                        <div class="address">
                            <v-icon>mdi-map-marker</v-icon>
                            <h4>{{ terms.location_term }}:</h4>
                            <p>{{ terms.contact_street }}, {{ terms.contact_city }}, {{ terms.contact_country }}</p>
                        </div>
                        <div class="email">
                            <v-icon>mdi-email</v-icon>
                            <h4>{{ terms.email_term }}</h4>
                            <p>{{ terms.contact_email_address }}</p>
                        </div>
                        <div class="phone">
                            <v-icon>mdi-phone</v-icon>
                            <h4>{{ terms.call_term }}:</h4>
                            <p>{{ terms.contact_phone1 }}</p>
                            <p>{{ terms.contact_phone2 }}</p>
                        </div>
                    </div>
                </v-col>
                <v-col id="contact-response" cols="12" sm="8">
                    <v-form ref="formRef" @submit.prevent="submitContactFormAsync" validate-on="submit lazy">
                        <v-container>
                            <v-row>
                                <v-col cols="12" md="6" class="py-1 py-md-2">
                                    <!-- Name -->
                                    <v-text-field v-model="contactForm.name" :rules="[rules.required]"
                                        :label="terms.contact_form_field_name" density="comfortable" variant="outlined" />
                                </v-col>
                                <v-col cols="12" md="6" class="py-1 py-md-2">
                                    <!-- Email -->
                                    <v-text-field v-model="contactForm.email" :rules="[rules.required, rules.email]"
                                        :label="terms.contact_form_field_email" density="comfortable" variant="outlined" />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" class="py-1 py-md-2">
                                    <!-- Subject -->
                                    <v-text-field v-model="contactForm.subject" :rules="[rules.required]"
                                        :label="terms.contact_form_field_subject" density="comfortable" variant="outlined" />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" class="py-1 py-md-2">
                                    <!-- Message -->
                                    <v-textarea v-model="contactForm.message" :rules="[rules.required]"
                                        :label="terms.contact_form_field_message" rows="3" max-rows="5" density="comfortable" variant="outlined" />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" class="text-center">
                                    <v-btn type="submit" color="avem" class="mt-2" :loading="showLoadingContactForm"
                                        large density="comfortable">
                                        {{ terms.send_message_term }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row v-if="hasContactFormSubmission">
                                <v-col>
                                    <div v-if="hasContactFormSuccess" class="sent-message">
                                        {{ terms.message_sent_success }}
                                        <br />
                                        {{ terms.message_sent_success_followup }}
                                    </div>
                                    <div v-else class="error-message justify-center">
                                        {{ terms.request_error }}
                                        <br />
                                        {{ terms.request_error_followup }}
                                        {{ terms.contact_email_address }}
                                    </div>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-col>
            </v-row>
        </v-container>
    </section>
</template>

<script setup>
import AOS from "aos";
import { onMounted, onUnmounted, reactive, ref, computed, inject } from "vue";
import { useStore } from "vuex";
import { useRouter } from 'vue-router';
import { useSwipePagination, useSwipeGesture } from '@/utils/useSwipePagination';
import aboutVideo from '@/assets/video/about_1.mp4';
import productsVideo from '@/assets/video/products_22.mp4';

const axios = inject('axios');
const store = useStore();
const router = useRouter();
const terms = computed(() => store.state.terms);
const rules = computed(() => store.state.validationRules);

const bannerProducts = [
    { id: 1, code: 'TM1', image: '/img/tape_dispenser_new.jpg', title: 'Tape Dispenser AVEM' },
    { id: 2, code: 'L1', image: '/img/L1.jpg', title: 'Band AVEM' },
    { id: 3, code: 'TS60', image: '/img/tape.png', title: 'Solvent Tape 48/60m' },
    { id: 6, code: 'TA60', image: '/img/60_acrill.jpg', title: 'Acrylic Tape 48/60m' },
    { id: 8, code: 'TA100', image: '/img/100_acrill.jpg', title: 'Acrylic Tape 48/100m' },
    { id: 7, code: 'TS100', image: '/img/100_solvent.jpg', title: 'Solvent Tape 48/100m' },
    { id: 9, code: 'TQ120', image: '/img/Power-120_quiet.jpg', title: 'Quiet Tape 48/120m' },
    { id: 13, code: 'TA60/6', image: '/img/60_acrill6.jpg', title: 'Acrylic Tape 6 pcs' },
    { id: 14, code: 'TA60MA1', image: '/img/TA60MA1.jpg', title: 'Dispenser + 1pc. Acrylic' },
    { id: 10, code: 'TA60PR6', image: '/img/TA60PR6.jpg', title: 'Dispenser + 6pcs. Acrylic' },
    { id: 4, code: 'TS60PR', image: '/img/promo_dispenser_and_tape.jpg', title: 'Dispenser + 6pcs. Solvent' },
    { id: 11, code: 'TQ120PR', image: '/img/TQ120PR.jpg', title: 'Dispenser + 3pcs. Quiet' },
    { id: 5, code: 'TA36PR', image: '/img/TA60PR36.jpg', title: 'Dispenser + 36pcs. Acrylic' },
];

const getBannerProductTitle = (item) => {
    if (!item) return '';
    const found = store.state.products?.find(p => p.id === item.id);
    if (found?.header) return found.header;
    return item.title || item.titleEn || '';
};

const navigateToProductOffer = (productId) => {
    router.push({ path: '/offers', query: { product: productId } });
};

const offersCurrentPage = ref(1);

const offersItemsPerPage = computed(() => {
    if (windowWidth.value < 600) return 6; // 2 cols x 3 rows = 6 items max per page
    if (windowWidth.value < 960) return 9; // 3 cols x 3 rows = 9 items max per page
    return 14; // Desktop fits all 13
});

const totalOffersPages = computed(() => {
    return Math.ceil(bannerProducts.length / offersItemsPerPage.value);
});

const offersSwipe = useSwipePagination(offersCurrentPage, totalOffersPages);

const paginatedOffersProducts = computed(() => {
    const start = (offersCurrentPage.value - 1) * offersItemsPerPage.value;
    const realItems = bannerProducts.slice(start, start + offersItemsPerPage.value);
    if (totalOffersPages.value <= 1) {
        return realItems;
    }
    const items = [...realItems];
    const totalNeeded = offersItemsPerPage.value;
    while (items.length < totalNeeded) {
        items.push({ isPlaceholder: true, id: `placeholder-offer-${items.length}` });
    }
    return items;
});

const allGalleryImages = computed(() => {
    const baseImages = terms.value?.portfolio_images || [];
    const productImages = [
        '/img/tape_dispenser_new.jpg',
        '/img/TQ120PR.jpg',
        '/img/Power-120_quiet.jpg',
        '/img/100_solvent.jpg',
        '/img/100_acrill.jpg',
        '/img/60_acrill.jpg',
        '/img/TA60MA1.jpg',
        '/img/L1.jpg',
        '/img/TA60PR36.jpg',
        '/img/60_acrill6.jpg',
        '/img/TA60PR6.jpg',
        '/img/promo_ester_dispenser_and_tape.jpeg',
        '/img/promo_dispenser_and_tape.jpg',
        '/img/products_11.jpg',
    ];
    const set = new Set([...productImages, ...baseImages]);
    return Array.from(set);
});

const contactForm = reactive({
    name: "",
    email: "",
    subject: "",
    message: "",
});

const showLoadingContactForm = ref(false);
const hasContactFormSubmission = ref(false);
const hasContactFormSuccess = ref(false);

const isLightboxOpen = ref(false);
const activeImageIndex = ref(0);
const currentVideo = ref(null);
const formRef = ref(null);

const showMoreProductText = ref(false);
const toggleShowMoreProductText = () => {
    showMoreProductText.value = !showMoreProductText.value;
};

const videoPaths = {
    about: aboutVideo,
    products: productsVideo,
};

const currentPage = ref(1);
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);

const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth;
};

const itemsPerPage = computed(() => {
    if (windowWidth.value < 600) return 6; // 2 cols x 3 rows = 6 items max
    if (windowWidth.value < 960) return 9; // 3 cols x 3 rows = 9 items max
    return 12; // 4 cols x 3 rows = 12 items max
});

const totalGalleryPages = computed(() => {
    return Math.ceil(allGalleryImages.value.length / itemsPerPage.value);
});

const gallerySwipe = useSwipePagination(currentPage, totalGalleryPages);

const paginatedGalleryImages = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const realImages = allGalleryImages.value.slice(start, start + itemsPerPage.value);
    const items = realImages.map(img => ({ isPlaceholder: false, src: img }));
    const totalNeeded = itemsPerPage.value;
    while (items.length < totalNeeded) {
        items.push({ isPlaceholder: true, src: '' });
    }
    return items;
});

const openLightbox = (index) => {
    activeImageIndex.value = index;
    isLightboxOpen.value = true;
};

const openLightboxFromPage = (localIndex) => {
    const globalIndex = (currentPage.value - 1) * itemsPerPage.value + localIndex;
    openLightbox(globalIndex);
};

const prevImage = () => {
    const total = allGalleryImages.value.length;
    if (total === 0) return;
    activeImageIndex.value = (activeImageIndex.value - 1 + total) % total;
};

const nextImage = () => {
    const total = allGalleryImages.value.length;
    if (total === 0) return;
    activeImageIndex.value = (activeImageIndex.value + 1) % total;
};

const lightboxSwipe = useSwipeGesture({
    onSwipeLeft: nextImage,
    onSwipeRight: prevImage,
});

const submitContactFormAsync = async () => {
    const validation = await formRef.value.validate();
    if (!validation.valid)
        return;

    showLoadingContactForm.value = true;
    try {
        let contactPayload = Object.keys(contactForm).reduce((acc, key) => {
            if (contactForm[key] !== null) {
                acc[key] = contactForm[key];
            }
            return acc;
        }, {});
        await axios.post('/contact', contactPayload);
        hasContactFormSuccess.value = true;
        formRef.value.reset();
    } catch (err) {
        console.error(err);
        hasContactFormSuccess.value = false;
    } finally {
        hasContactFormSubmission.value = true;
        showLoadingContactForm.value = false;
        router.push({ path: '/', hash: '#contact-response' });
    }
};

const navigateToRoute = (route) => {
    router.push(route);
};

// Enable autoplay on scroll
const enableAutoplayOnScroll = (videoId, autoplayOnLoad) => {
    const videoElement = document.getElementById(videoId);
    if (!videoElement) {
        console.warn(`Video element with id "${videoId}" not found.`);
        return;
    }

    videoElement.muted = true; // Mute the video initially
    videoElement.volume = 0.1; // Set volume

    const observerOptions = {
        root: null, // Relative to document viewport
        rootMargin: "0px", // Margin around root
        threshold: 1, // Fully visible
    };

    const callback = (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                if (currentVideo.value && currentVideo.value !== videoElement) {
                    if (!currentVideo.value.paused) {
                        currentVideo.value.pause(); // Pause the currently playing video
                    }
                }

                if (videoElement.paused) {
                    videoElement.play()
                        .catch((err) => {
                            console.warn("Failed to play the video:", err);
                        });
                    currentVideo.value = videoElement; // Update the currently playing video
                }
            } else {
                if (!videoElement.paused) {
                    videoElement.pause();
                }
            }
        });
    };

    const observer = new IntersectionObserver(callback, observerOptions);
    observer.observe(videoElement);

    if (autoplayOnLoad) {
        videoElement.play()
            .catch((err) => {
                console.warn("Failed to autoplay on load:", err);
            });
        currentVideo.value = videoElement;
    }
};

const productsList = ref([]);
const loadingProducts = ref(true);
const selectedQuantities = reactive({});
const favorites = computed(() => store.state.favorites);
const snackbar = ref(false);
const snackbarText = ref('');

const formatNumber = (val) => {
    if (val === undefined || val === null) return '0.00';
    return Number(val).toFixed(2).replace('.', ',');
};

const getProductsAsync = async () => {
    loadingProducts.value = true;
    try {
        const data = await store.dispatch('fetchProducts', { axios });
        productsList.value = data || [];
        productsList.value.forEach(p => {
            selectedQuantities[p.id] = 1;
        });
    } catch (err) {
        console.error("Failed to load products list:", err);
    } finally {
        loadingProducts.value = false;
    }
};

const cartItems = computed(() => store.state.cart);

const getCartQty = (productId) => {
    const item = cartItems.value.find(i => i.id === productId);
    return item ? item.quantity : 0;
};

const getQty = (id) => {
    if (selectedQuantities[id] !== undefined) {
        return selectedQuantities[id];
    }
    const inCart = getCartQty(id);
    return inCart > 0 ? inCart : 1;
};

const isAddToCartDisabled = (product) => {
    return getQty(product.id) === getCartQty(product.id);
};

const getAddToCartButtonText = (product) => {
    const inCart = getCartQty(product.id);
    if (inCart > 0) {
        return terms.value.update_cart || 'Update cart';
    }
    return terms.value.add_to_cart || 'Add to cart';
};

const incrementQty = (id) => {
    const current = getQty(id);
    if (current < 100) selectedQuantities[id] = current + 1;
};

const decrementQty = (id) => {
    const current = getQty(id);
    if (current > 1) selectedQuantities[id] = current - 1;
};

const toggleFavorite = (id) => {
    store.commit('toggleFavorite', id);
};

const onAddToCart = (product) => {
    const qty = getQty(product.id);
    selectedQuantities[product.id] = qty;
    store.commit('addToCart', { product, quantity: qty });
    snackbarText.value = `${product.header} (${qty} ${terms.value.items}) -> ${terms.value.added_to_cart}`;
    snackbar.value = true;
};

const onRemoveFromCart = (product) => {
    store.commit('removeFromCart', product.id);
    selectedQuantities[product.id] = 1;
    snackbarText.value = `${product.header} -> ${terms.value.removed_from_cart}`;
    snackbar.value = true;
};

const goToCart = () => {
    router.push('/order');
};

const scrollToProductsList = () => {
    const el = document.getElementById('products-list');
    if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
    }
};

const copyTextToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
    } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Fallback copy failed', err);
        }
        document.body.removeChild(textArea);
    }
};

const shareProduct = async (product) => {
    const shareUrl = `${window.location.origin}${window.location.pathname}#products-list`;
    const productInfo = `${product.header} (${product.priceEuro}€ / ${product.priceLeva} ${terms.value?.currency_leva || 'BGN'})`;
    const fullShareText = `${productInfo}\n${shareUrl}`;

    try {
        await copyTextToClipboard(fullShareText);
    } catch (e) {
        console.warn('Clipboard copy failed:', e);
    }

    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(productInfo)}`;
    window.open(fbShareUrl, '_blank');

    snackbarText.value = `${productInfo} -> ${terms.value.copied || 'Copied to clipboard!'}`;
    snackbar.value = true;
};

const handleKeyDown = (e) => {
    if (!isLightboxOpen.value) return;
    if (e.key === 'Escape' || e.code === 'Escape') {
        isLightboxOpen.value = false;
    } else if (e.key === 'ArrowLeft' || e.code === 'ArrowLeft') {
        prevImage();
    } else if (e.key === 'ArrowRight' || e.code === 'ArrowRight') {
        nextImage();
    }
};

onMounted(async () => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updateWindowWidth);
    AOS.init({
        duration: 1000,
        easing: "ease-in-out",
        once: true,
        mirror: false
    });
    enableAutoplayOnScroll("VidAbout", true);
    enableAutoplayOnScroll("VidProducts", false);
    await getProductsAsync();
});

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('resize', updateWindowWidth);
});
</script>

<style scoped>
/* PAGE-CONTENT */

@import 'aos/dist/aos.css';

#products-list {
    scroll-margin-top: 64px;
}

.product-item-card {
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05) !important;
    transform: translateY(2px);
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    background: #ffffff;
}

.product-item-card:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(0px);
}

.product-main-title {
    white-space: normal !important;
    word-break: break-word;
}

.product-main-title h3 {
    color: #1a252f;
    line-height: 1.3;
}

.price-display {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c5282;
}

.price-highlight {
    color: #2b6cb0;
}

.favorite-btn {
    background: none;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    margin-left: 12px;
}

.purchase-options-box {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}

.quantity-stepper {
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff;
}

.step-btn {
    width: 28px;
    height: 32px;
    background: #edf2f7;
    border: none;
    font-weight: bold;
    font-size: 16px;
    cursor: pointer;
    color: #4a5568;
    transition: background 0.2s;
}

.step-btn:hover:not(:disabled) {
    background: #cbd5e0;
}

.qty-input {
    width: 44px;
    height: 32px;
    text-align: center;
    border: none;
    border-left: 1px solid #cbd5e0;
    border-right: 1px solid #cbd5e0;
    font-weight: 600;
    font-size: 14px;
    outline: none;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.add-to-cart-gold-btn {
    font-size: 0.95rem;
    padding: 10px 24px;
}

.border-top {
    border-top: 1px solid #edf2f7;
}

.description-text {
    font-size: 0.92rem;
    line-height: 1.6;
    color: #4a5568;
}

.bullet-list {
    font-size: 0.88rem;
    color: #2d3748;
    line-height: 1.5;
}

.bullet-list li {
    margin-bottom: 4px;
}

section {
    padding: 80px 0;
}

.restricted-width {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
}

/* Hero Section */
#hero {
    width: 100%;
    background: url('/img/slogon_1.jpg') center 38% / cover no-repeat !important;
    position: relative;
    padding-top: 35px !important;
    padding-bottom: 45px !important;
    min-height: 580px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

#hero:before {
    content: "";
    background: radial-gradient(circle at center, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.72) 100%) !important;
    position: absolute;
    bottom: 0;
    top: 0;
    left: 0;
    right: 0;
    pointer-events: none;
}

#hero .container {
    position: relative;
    z-index: 2;
    text-align: center;
}

.hero-top-title {
    margin: 0 0 10px 0 !important;
    font-size: clamp(1.3rem, 2.2vw, 2.05rem) !important;
    font-weight: 700 !important;
    line-height: 1.35 !important;
    color: #ffffff !important;
    font-family: "Poppins", sans-serif;
    text-shadow: 0 3px 14px rgba(0, 0, 0, 0.95), 0 1px 3px rgba(0, 0, 0, 0.8) !important;
    white-space: nowrap !important;
}

.hero-brand-inline {
    color: #ffc451 !important;
    font-weight: 800 !important;
    text-shadow: 0 4px 16px rgba(0, 0, 0, 0.95), 0 0 15px rgba(255, 196, 81, 0.4) !important;
}

.hero-image-spacer {
    height: clamp(150px, 19vw, 220px) !important;
    transition: height 0.3s ease;
}

.hero-subtitle {
    color: rgba(255, 255, 255, 0.95) !important;
    margin: 12px auto 0 auto !important;
    font-size: clamp(0.85rem, 1.1vw, 1.05rem) !important;
    font-weight: 500 !important;
    line-height: 1.48 !important;
    max-width: 820px !important;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.95) !important;
}

@media (min-width: 1024px) {
    #hero {
        background-attachment: scroll !important;
    }
}

@media (max-width: 959px) {
    #hero {
        min-height: 360px !important;
        padding-top: 20px !important;
        padding-bottom: 20px !important;
    }

    .hero-top-title {
        font-size: 22px !important;
        line-height: 1.3 !important;
        white-space: normal !important;
    }

    .hero-image-spacer {
        height: 120px !important;
    }

    .hero-subtitle {
        font-size: 14px !important;
        line-height: 1.4 !important;
    }
}

@media (max-width: 600px) {
    #hero {
        min-height: 260px !important;
        padding-top: 15px !important;
        padding-bottom: 15px !important;
    }

    .hero-top-title {
        font-size: 18px !important;
    }

    .hero-image-spacer {
        height: 85px !important;
    }

    .hero-subtitle {
        font-size: 12.5px !important;
    }
}

/* About */
#about .content h3 {
    font-weight: 700;
    font-size: 28px;
    font-family: "Poppins", sans-serif;
}

#about .content ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

#about .content ul li {
    padding: 6px 0;
    position: static;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.95rem;
    color: #374151;
    line-height: 1.65;
}

#about .content ul i,
#about .content ul .v-icon,
#products .content ul i,
#products .content ul .v-icon {
    position: static !important;
    top: auto !important;
    left: auto !important;
    font-size: 18px !important;
    color: #ffc451;
}

#about .content p:last-child {
    margin-bottom: 0;
}

@media (max-width: 959px) {
    #about .content h3 {
        font-size: 19px !important;
        line-height: 1.35 !important;
        margin-bottom: 8px !important;
    }

    #about .content p,
    .fst-italic {
        font-size: 0.88rem !important;
        line-height: 1.45 !important;
        margin-bottom: 10px !important;
    }

    #about .content ul li,
    #products .content ul li {
        font-size: 0.84rem !important;
        padding-bottom: 6px !important;
    }

    #about .content ul i,
    #products .content ul i,
    #about .content ul .v-icon,
    #products .content ul .v-icon {
        font-size: 17px !important;
        position: static !important;
        top: auto !important;
        left: auto !important;
    }
}

@media (max-width: 600px) {
    #about .content h3 {
        font-size: 16px !important;
        line-height: 1.3 !important;
    }

    #about .content p,
    .fst-italic {
        font-size: 0.8rem !important;
        line-height: 1.4 !important;
    }

    #about .content ul li,
    #products .content ul li {
        font-size: 0.78rem !important;
        padding-bottom: 4px !important;
    }

    #about .content ul i,
    #products .content ul i {
        font-size: 15px !important;
    }

    .product-main-heading {
        font-size: 1.25rem !important;
    }

    .product-lead-text {
        font-size: 0.95rem !important;
    }

    .product-desc-p,
    .product-feature-list li,
    .product-specs-list li,
    .product-b2b-list li,
    .product-b2b-intro {
        font-size: 0.82rem !important;
        line-height: 1.45 !important;
    }

    .product-subheading,
    .product-b2b-heading {
        font-size: 1rem !important;
    }

    .product-b2b-subheading {
        font-size: 0.92rem !important;
    }
}

/* Products Content & SEO Sections */
.product-main-heading {
    font-size: 1.55rem;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    margin-bottom: 8px;
}

.product-lead-text {
    font-size: 1.05rem;
    font-weight: 500;
    color: #374151;
    line-height: 1.6;
    margin-bottom: 12px;
}

.product-desc-p {
    font-size: 0.95rem;
    color: #374151;
    line-height: 1.65;
    white-space: pre-line;
}

.product-subheading {
    font-size: 1.12rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.75rem;
}

#products .product-feature-list,
#products .product-specs-list,
#products .product-b2b-list {
    padding-left: 0 !important;
    list-style: none !important;
    margin: 0 !important;
}

#products .product-feature-list li,
#products .product-specs-list li,
#products .product-b2b-list li {
    padding: 6px 0 !important;
    position: static !important;
    display: flex !important;
    align-items: flex-start !important;
    gap: 10px !important;
    font-size: 0.95rem !important;
    color: #374151 !important;
    line-height: 1.65 !important;
}

#products .product-feature-list li strong,
#products .product-specs-list li strong,
#products .product-b2b-list li strong {
    font-weight: 600 !important;
    color: #111827 !important;
}

.product-specs-card,
.product-specs-section,
.product-b2b-card,
.product-b2b-section {
    background: transparent;
    border: none;
    box-shadow: none;
}

.specs-tagline {
    font-weight: 600;
    color: #111827;
    font-size: 0.92rem;
}

.specs-contact {
    font-size: 0.92rem;
    color: #374151;
    font-weight: 500;
}

.specs-contact a {
    color: #111827;
    text-decoration: underline;
    font-weight: 600;
    transition: color 0.2s ease;
}

.specs-contact a:hover {
    color: #ffc451;
}

#products .image {
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover !important;
    min-height: 400px;
}

/* Portfolio Lightbox Modal */
:deep(.v-overlay__content:has(.lightbox-container)) {
    background: rgba(10, 10, 10, 0.96) !important;
    backdrop-filter: blur(12px) !important;
}

.lightbox-container {
    position: relative;
    background: rgba(10, 10, 10, 0.96) !important;
    backdrop-filter: blur(12px) !important;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh !important;
    width: 100vw !important;
    max-width: 100vw !important;
    padding: 0 !important;
    margin: 0 !important;
    overflow: hidden;
}

.lightbox-image-wrapper {
    max-width: 85vw;
    max-height: 85vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
}

.lightbox-active-img {
    max-width: 80vw;
    max-height: 78vh;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 24px !important;
    border: 2.5px solid rgba(255, 196, 81, 0.85) !important;
    box-shadow: 0 16px 45px rgba(0, 0, 0, 0.85), 0 0 32px rgba(255, 196, 81, 0.4) !important;
    background: #000000;
    transition: all 0.3s ease;
}

.lightbox-close-circle-btn {
    position: absolute;
    top: 22px;
    right: 24px;
    z-index: 100;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: rgba(25, 25, 25, 0.9);
    border: 2px solid #ffc451;
    color: #ffc451;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.6), 0 0 12px rgba(255, 196, 81, 0.3);
}

.lightbox-close-circle-btn:hover {
    background: #ffc451;
    color: #000000;
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(255, 196, 81, 0.6);
}

.lightbox-nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 90;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(28, 28, 28, 0.92) 0%, rgba(15, 15, 15, 0.95) 100%);
    border: 2px solid #ffc451;
    color: #ffc451;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.6), 0 0 15px rgba(255, 196, 81, 0.25);
}

.lightbox-prev-btn {
    left: 24px;
}

.lightbox-next-btn {
    right: 24px;
}

.lightbox-nav-btn:hover {
    background: linear-gradient(135deg, #ffc451 0%, #e5a730 100%);
    color: #000000;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 8px 24px rgba(255, 196, 81, 0.55);
}

@media (max-width: 600px) {
    .lightbox-nav-btn {
        width: 40px;
        height: 40px;
    }
    .lightbox-prev-btn {
        left: 10px;
    }
    .lightbox-next-btn {
        right: 10px;
    }
    .lightbox-close-circle-btn {
        top: 14px;
        right: 14px;
        width: 34px;
        height: 34px;
    }
}

.gallery-placeholder-slot {
    width: 100%;
    aspect-ratio: 1.5;
    visibility: hidden;
    pointer-events: none;
}

:deep(.gallery-pagination .v-pagination__item--is-active .v-btn) {
    background: linear-gradient(135deg, #ffc451 0%, #e5a730 100%) !important;
    color: #000000 !important;
    font-weight: 700 !important;
    box-shadow: 0 4px 12px rgba(255, 196, 81, 0.45) !important;
}

:deep(.gallery-pagination .v-btn) {
    border-radius: 50% !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

:deep(.gallery-pagination .v-btn:hover) {
    transform: translateY(-2px) scale(1.08) !important;
}

/* Contact */
#contact .form-overlay {
    z-index: 10;
    /* Ensure it appears above the form content */
}

#contact .info {
    width: 100%;
    background: #fff;
}

#contact .info i {
    font-size: 20px;
    background: #ffc451;
    color: #151515;
    float: left;
    width: 44px;
    height: 44px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    transition: all 0.3s ease-in-out;
}

#contact .info h4 {
    padding: 0 0 0 60px;
    font-size: 22px;
    font-weight: 600;
    margin-bottom: 5px;
    color: #151515;
}

#contact .info p {
    padding: 0 0 0 60px;
    margin-bottom: 0;
    font-size: 14px;
    color: #484848;
}

#contact .info .email,
#contact .info .phone {
    margin-top: 40px;
}

@media (max-width: 959px) {
    #contact .info {
        margin-bottom: 20px;
    }

    #contact .info i {
        width: 36px !important;
        height: 36px !important;
        font-size: 17px !important;
        border-radius: 6px !important;
    }

    #contact .info h4 {
        padding-left: 48px !important;
        font-size: 16px !important;
        margin-bottom: 2px !important;
    }

    #contact .info p {
        padding-left: 48px !important;
        font-size: 13px !important;
        line-height: 1.35 !important;
    }

    #contact .info .email,
    #contact .info .phone {
        margin-top: 16px !important;
    }
}

@media (max-width: 600px) {
    #contact .info i {
        width: 32px !important;
        height: 32px !important;
        font-size: 15px !important;
    }

    #contact .info h4 {
        padding-left: 42px !important;
        font-size: 15px !important;
    }

    #contact .info p {
        padding-left: 42px !important;
        font-size: 12.5px !important;
    }

    #contact .info .email,
    #contact .info .phone {
        margin-top: 12px !important;
    }
}

#contact .sent-message {
    margin-top: 20px;
    color: #fff;
    background: #48af77;
    text-align: center;
    padding: 15px;
    font-weight: 600;
}

#contact .error-message {
    margin-top: 20px;
    color: #fff;
    background: #ed3c0d;
    text-align: left;
    padding: 15px;
    font-weight: 600;
}


.order-now-btn {
    font-size: 18px;
    padding: 15px 35px;
    position: relative;
    overflow: hidden;
    margin: 20px;
}

@media (max-width: 959px) {
    .order-now-btn {
        font-size: 15px !important;
        padding: 10px 24px !important;
        margin: 10px auto 4px auto !important;
    }
}

.order-now-btn::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transition: 0.4s;
}

.order-now-btn:hover::after {
    left: 100%;
}

.show-more-toggle-btn {
    background: rgba(20, 20, 20, 0.9);
    color: #ffc451;
    border: 1.5px solid rgba(255, 196, 81, 0.6);
    border-radius: 20px;
    padding: 6px 18px;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.25s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.show-more-toggle-btn:hover {
    background: #ffc451;
    color: #000000;
    box-shadow: 0 6px 16px rgba(255, 196, 81, 0.4);
    transform: translateY(-1px);
}

/* Signature Arc & Corner Rounding for Home Media Elements */
.signature-media-frame,
video#VidAbout,
video#VidProducts {
    aspect-ratio: 16 / 9;
    object-fit: cover;
    background-color: #1a1a1a;
    border-radius: 24px !important;
    border: 2px solid rgba(255, 196, 81, 0.75) !important;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45), 0 0 22px rgba(255, 196, 81, 0.2) !important;
    overflow: hidden !important;
    transition: transform 0.35s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.35s cubic-bezier(0.25, 1, 0.5, 1), border-color 0.35s ease !important;
}

.signature-media-frame:hover,
video#VidAbout:hover,
video#VidProducts:hover {
    transform: translateY(-4px) scale(1.015) !important;
    border-color: #ffc451 !important;
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.6), 0 0 32px rgba(255, 196, 81, 0.45) !important;
}

/* Portfolio Gallery Styling with Pure White Background */
#portfolio {
    background: #ffffff !important;
}

#portfolio .v-container {
    background: #ffffff !important;
}

.lightbox-image {
    background: #ffffff !important;
    background-color: #ffffff !important;
    border-radius: 16px !important;
    border: 1.5px solid rgba(255, 196, 81, 0.45) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(255, 196, 81, 0.12) !important;
    overflow: hidden !important;
    cursor: pointer;
    transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.3s cubic-bezier(0.25, 1, 0.5, 1), border-color 0.3s ease !important;
}

.lightbox-image:hover {
    transform: translateY(-2px) scale(1.02) !important;
    border-color: #ffc451 !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(255, 196, 81, 0.25) !important;
}

.lightbox-image :deep(.v-img__img),
.lightbox-image :deep(img) {
    background: #ffffff !important;
}

@media (max-width: 600px) {
    .lightbox-image {
        border-radius: 12px !important;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03) !important;
    }
}

.offers-outer-title {
    font-size: 22px;
    font-weight: 600;
    font-family: "Poppins", "Raleway", sans-serif;
    color: #1a1a1a;
    margin-bottom: 22px;
    max-width: 850px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.4;
}

@media (max-width: 920px) {
    .offers-outer-title {
        font-size: 16.5px !important;
        margin-bottom: 14px !important;
    }
}

@media (max-width: 480px) {
    .offers-outer-title {
        font-size: 14.5px !important;
        margin-bottom: 12px !important;
    }
}

/* Offers CTA Section Signature Border & Interactive Product Tiles Grid */
.signature-offers-card {
    position: relative;
    background: transparent;
    padding: 0;
    border: none !important;
    box-shadow: none !important;
}

.swipeable-container {
    touch-action: pan-y;
    user-select: none;
    -webkit-user-select: none;
    width: 100%;
    overflow: hidden;
}

.swipe-next-enter-active,
.swipe-next-leave-active,
.swipe-prev-enter-active,
.swipe-prev-leave-active {
    transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.25, 1, 0.5, 1);
}

.swipe-next-enter-from {
    opacity: 0;
    transform: translateX(30px);
}
.swipe-next-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.swipe-prev-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}
.swipe-prev-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

@keyframes bounceEndLeft {
    0% { transform: translateX(0); box-shadow: inset 0px 0 0px transparent; }
    30% { transform: translateX(-22px); box-shadow: inset -8px 0 16px -3px rgba(255, 196, 81, 0.65); }
    60% { transform: translateX(5px); box-shadow: inset -4px 0 8px -3px rgba(255, 196, 81, 0.35); }
    80% { transform: translateX(-2px); box-shadow: inset -1px 0 4px -2px rgba(255, 196, 81, 0.15); }
    100% { transform: translateX(0); box-shadow: inset 0px 0 0px transparent; }
}

@keyframes bounceEndRight {
    0% { transform: translateX(0); box-shadow: inset 0px 0 0px transparent; }
    30% { transform: translateX(22px); box-shadow: inset 8px 0 16px -3px rgba(255, 196, 81, 0.65); }
    60% { transform: translateX(-5px); box-shadow: inset 4px 0 8px -3px rgba(255, 196, 81, 0.35); }
    80% { transform: translateX(2px); box-shadow: inset 1px 0 4px -2px rgba(255, 196, 81, 0.15); }
    100% { transform: translateX(0); box-shadow: inset 0px 0 0px transparent; }
}

.bounce-end-left {
    animation: bounceEndLeft 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.bounce-end-right {
    animation: bounceEndRight 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.product-tiles-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-content: flex-start;
    gap: 12px;
    width: 100%;
    min-height: 332px;
}

.offers-placeholder-tile {
    visibility: hidden !important;
    pointer-events: none !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
}

.interactive-product-tile {
    position: relative;
    flex: 1 1 calc(25% - 12px);
    min-width: 200px;
    max-width: 280px;
    height: 160px;
    border-radius: 16px !important;
    overflow: hidden !important;
    isolation: isolate;
    -webkit-mask-image: -webkit-radial-gradient(white, black);
    mask-image: radial-gradient(white, black);
    background: #ffffff;
    border: 1.5px solid rgba(255, 196, 81, 0.4);
    cursor: pointer;
    transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

@media (max-width: 860px) {
    .product-tiles-grid {
        min-height: 302px;
    }
    .interactive-product-tile {
        flex: 1 1 calc(33.333% - 10px);
        min-width: 160px;
        height: 145px;
    }
}

@media (max-width: 540px) {
    .product-tiles-grid {
        min-height: 272px;
    }
    .interactive-product-tile {
        flex: 1 1 calc(50% - 8px);
        min-width: 130px;
        height: 130px;
        border-radius: 12px !important;
    }
}

.tile-image-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    transition: transform 0.4s ease, filter 0.4s ease;
}

@property --tile-border-angle {
    syntax: "<angle>";
    inherits: false;
    initial-value: 0deg;
}

@keyframes spinTileBorder {
    to {
        --tile-border-angle: 360deg;
    }
}

.interactive-product-tile::before {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 18px;
    padding: 3px;
    background: conic-gradient(
        from var(--tile-border-angle, 0deg),
        transparent 0deg,
        rgba(255, 196, 81, 0.4) 60deg,
        #ffc451 120deg,
        #ffffff 140deg,
        #ffc451 160deg,
        rgba(255, 196, 81, 0.4) 220deg,
        transparent 280deg
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 10;
    animation: spinTileBorder 3.5s linear infinite;
}

.interactive-product-tile:hover::before {
    opacity: 1;
}

.interactive-product-tile:hover {
    transform: translateY(-6px) scale(1.03);
    border-color: #ffc451 !important;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12), 0 0 25px rgba(255, 196, 81, 0.5) !important;
    z-index: 5;
}

.interactive-product-tile:hover .tile-image-bg {
    transform: scale(1.12);
    filter: brightness(1.12);
}

.tile-hover-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle, rgba(255, 196, 81, 0.25) 0%, rgba(0, 0, 0, 0.6) 100%);
    opacity: 0;
    transition: opacity 0.35s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.interactive-product-tile:hover .tile-hover-overlay {
    opacity: 1;
}

.tile-hover-icon {
    background: #ffc451;
    color: #111;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
    transform: scale(0.7);
    transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.interactive-product-tile:hover .tile-hover-icon {
    transform: scale(1);
}

.tile-badge {
    position: relative;
    z-index: 3;
    background: rgba(20, 20, 20, 0.94);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    padding: 8px 8px;
    border-top: 1.5px solid rgba(255, 196, 81, 0.45);
    border-bottom-left-radius: 14px !important;
    border-bottom-right-radius: 14px !important;
    text-align: center;
    width: 100%;
    min-height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
}

@media (max-width: 540px) {
    .tile-badge {
        border-bottom-left-radius: 10px !important;
        border-bottom-right-radius: 10px !important;
        padding: 6px 4px;
        min-height: 38px;
    }
}

.tile-title {
    font-size: 0.82rem;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.2px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.25;
    text-align: center;
    transition: color 0.2s ease;
}

.interactive-product-tile:hover .tile-title {
    color: #ffc451;
}

@media (max-width: 540px) {
    .tile-title {
        font-size: 0.74rem;
        line-height: 1.2;
    }
}

.offers-gold-btn {
    font-size: 1rem !important;
    padding: 12px 36px !important;
    margin: 0 !important;
}

@media (max-width: 600px) {
    .offers-gold-btn {
        font-size: 0.92rem !important;
        padding: 10px 22px !important;
        max-width: 90% !important;
    }
}


/* Animation */
@-webkit-keyframes animate-loading {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes animate-loading {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>